import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePremiumStatus } from "@/context/PremiumStatusContext";
import { PremiumSoonHero } from "@/components/PremiumSoon";
import { usePremiumSoon } from "@/components/PremiumSoon";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { PremiumBadge } from "@/components/Badges";
import {
  Crown, Check, X, TrendingUp, Image as ImageIcon, Sparkles, Trophy,
  Lock, Loader2, XCircle, CheckCircle2, Users, Eye, Calendar, Zap, Star,
  Flame, ArrowRight, Shield,
} from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  {
    icon: TrendingUp,
    title: "Visibilité prioritaire",
    desc: "Votre profil remonte en tête des recherches pros et apparaît avant les talents Free.",
  },
  {
    icon: Crown,
    title: "Badge Premium doré",
    desc: "Une distinction visible partout sur Helencia. Les marques savent qui sont les talents engagés.",
  },
  {
    icon: ImageIcon,
    title: "Portfolio illimité",
    desc: "Photos illimitées, projets, séries éditoriales, blocs créatifs — racontez votre univers sans contrainte.",
  },
  {
    icon: Sparkles,
    title: "Accès prioritaire aux collaborations",
    desc: "Postulez 24h avant les talents Free. Plus de places, plus de chances.",
  },
  {
    icon: Trophy,
    title: "Avantages concours",
    desc: "Inscription prioritaire, badge dédié dans la galerie, double visibilité sur la fiche concours.",
  },
  {
    icon: Calendar,
    title: "Événements exclusifs",
    desc: "Accès aux soirées Helencia, masterclasses et networking privé avec les marques partenaires.",
  },
];

export default function PremiumPage() {
  const { user, profile, loading, checkAuth } = useAuth();
  const { comingSoon } = usePremiumStatus();
  const { open: openPremiumSoon } = usePremiumSoon();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled") === "1";

  const [starting, setStarting] = useState(false);
  const [statusView, setStatusView] = useState(null);
  const [txn, setTxn] = useState(null);
  const [proof, setProof] = useState({ premium_talents: 0, total_talents: 0, profile_views_week: 0, active_opportunities: 0 });

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/premium/social-proof").then(({ data }) => setProof(data)).catch(() => {});
  }, []);

  // Polling upon return from Stripe (success flow)
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let stopped = false;
    setStatusView("polling");
    const poll = async () => {
      try {
        const { data } = await api.get(`/premium/status/${sessionId}`);
        setTxn(data);
        if (data.payment_status === "paid") {
          setStatusView("success");
          await checkAuth();
          return;
        }
        if (data.status === "expired") { setStatusView("expired"); return; }
      } catch (e) { /* retry */ }
      attempts++;
      if (attempts < 12 && !stopped) setTimeout(poll, 2000);
      else if (!stopped) setStatusView("error");
    };
    poll();
    return () => { stopped = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const startCheckout = async () => {
    if (comingSoon) {
      openPremiumSoon();
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post("/premium/checkout", {
        package_id: "monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      if (err.response?.data?.detail === "premium_coming_soon") {
        openPremiumSoon();
      } else {
        toast.error(err.response?.data?.detail || "Erreur paiement");
      }
      setStarting(false);
    }
  };

  const clearSession = () => {
    searchParams.delete("session_id");
    searchParams.delete("cancelled");
    setSearchParams(searchParams);
    setStatusView(null);
    setTxn(null);
  };

  if (!user || !profile) return null;
  const isPremium = profile.is_premium;

  return (
    <DashboardLayout>
      {comingSoon ? (
        <PremiumSoonHero />
      ) : (
        <>
          {/* ====== Checkout status banners ====== */}
      {statusView === "polling" && (
        <div className="mb-8 rounded-3xl bg-gradient-to-r from-[#FAF3EA] to-white border border-[#E6C29A] p-6 flex items-center gap-4" data-testid="checkout-polling">
          <Loader2 className="w-6 h-6 text-[#C68E59] animate-spin" />
          <div>
            <p className="font-serif text-xl text-[#1A1A1A]">Vérification du paiement…</p>
            <p className="text-sm text-[#6E4B2A]">Nous confirmons votre transaction Stripe, cela prend quelques secondes.</p>
          </div>
        </div>
      )}
      {statusView === "success" && (
        <div className="mb-8 rounded-3xl bg-[#1A1A1A] text-white p-8 flex items-center gap-5" data-testid="checkout-success">
          <div className="w-12 h-12 rounded-full bg-[#C68E59] flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="caption-label text-[#E6C29A]">Paiement confirmé</p>
            <p className="font-serif text-2xl leading-tight mt-1">Bienvenue en Premium !</p>
            <p className="text-sm text-white/75 mt-1">Vos avantages sont actifs. Profitez-en dès maintenant.</p>
          </div>
          <Button onClick={clearSession} className="rounded-full bg-white text-[#1A1A1A] hover:bg-[#FAF3EA]">OK</Button>
        </div>
      )}
      {(statusView === "expired" || statusView === "error" || cancelled) && (
        <div className="mb-8 rounded-3xl bg-[#F6E6E3] border border-[#E6C0BA] p-6 flex items-center gap-4" data-testid="checkout-error">
          <XCircle className="w-6 h-6 text-[#A3483F]" />
          <div className="flex-1">
            <p className="font-serif text-xl text-[#1A1A1A]">{cancelled ? "Paiement annulé" : "Session expirée"}</p>
            <p className="text-sm text-[#6E4B2A]">Aucun prélèvement n'a été effectué.</p>
          </div>
          <Button onClick={clearSession} variant="outline" className="rounded-full border-[#C68E59]">Fermer</Button>
        </div>
      )}

      {/* ============ HERO ============ */}
      <section className="relative rounded-[32px] overflow-hidden bg-[#0E0B08] text-white mb-12" data-testid="premium-hero">
        {/* Decorative gradients */}
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#C68E59]/30 blur-[140px]" />
          <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#A26F3E]/25 blur-[120px]" />
        </div>

        <div className="relative px-6 sm:px-12 lg:px-16 py-14 sm:py-20 lg:py-24 max-w-5xl">
          {/* Urgency tag */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C68E59]/15 border border-[#C68E59]/30 text-[11px] uppercase tracking-[0.18em] text-[#E6C29A]" data-testid="urgency-tag">
            <Flame className="w-3.5 h-3.5" /> Offre de lancement · Accès limité
          </div>

          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight max-w-4xl">
            Passe au niveau supérieur avec <span className="italic text-[#E6C29A]">Helencia Premium</span>
          </h1>
          <p className="text-base sm:text-xl mt-5 text-white/80 max-w-2xl leading-relaxed">
            Plus de visibilité, plus de collaborations, plus d'opportunités.
          </p>

          {/* Live social proof row */}
          <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl" data-testid="social-proof-row">
            <ProofStat icon={Users} value={proof.premium_talents} label="talents Premium" />
            <ProofStat icon={Eye} value={proof.profile_views_week} label="vues cette semaine" />
            <ProofStat icon={Sparkles} value={proof.active_opportunities} label="opportunités actives" />
          </div>

          {/* Status card if Premium */}
          {isPremium && (
            <div className="mt-10 inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-[#C68E59]/15 border border-[#C68E59]/35" data-testid="premium-active-pill">
              <PremiumBadge />
              <span className="text-sm">
                Vous êtes Premium {profile.premium_expires_at && `· jusqu'au ${new Date(profile.premium_expires_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}`}
              </span>
            </div>
          )}
        </div>
      </section>
        </>
      )}

      {/* ============ TRANSFORMATION (Avant / Après) ============ */}
      <section className="mb-16" data-testid="transformation-section">
        <div className="text-center mb-10">
          <p className="caption-label text-[#C68E59]">La transformation</p>
          <h2 className="font-serif text-3xl sm:text-5xl text-[#1A1A1A] mt-2 leading-tight">
            Avant <span className="text-[#A26F3E] italic">/</span> Après
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <div className="rounded-3xl bg-white border border-[#E5E5E5] p-7 sm:p-8 relative" data-testid="state-free">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FAF3EA] text-[#8B8680] text-[10px] uppercase tracking-wider font-semibold">
              Avant · Free
            </div>
            <h3 className="font-serif text-2xl text-[#1A1A1A] mt-4 leading-tight">Vous attendez d'être trouvé</h3>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                "Visibilité limitée dans la recherche",
                "Moins de candidatures envoyées",
                "Portfolio plafonné à 12 photos",
                "Accès standard aux collaborations",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-[#6E6E6E]">
                  <X className="w-4 h-4 text-[#A3483F] mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-gradient-to-br from-[#1A1A1A] via-[#221912] to-[#0F0907] text-white p-7 sm:p-8 relative shadow-[0_30px_80px_-30px_rgba(198,142,89,0.4)]" data-testid="state-premium">
            <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C68E59] text-white text-[10px] uppercase tracking-wider font-semibold">
              <Crown className="w-3 h-3" /> Après · Premium
            </div>
            <h3 className="font-serif text-2xl mt-4 leading-tight">Les marques viennent à vous</h3>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                "Visibilité boostée — premier dans les résultats",
                "Plus d'opportunités, candidatures prioritaires",
                "Profil mis en avant chez les pros qui recrutent",
                "Portfolio illimité + accès exclusif",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-white/85">
                  <Check className="w-4 h-4 text-[#E6C29A] mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className="mb-16" data-testid="benefits-section">
        <div className="text-center mb-10">
          <p className="caption-label text-[#C68E59]">Vos avantages</p>
          <h2 className="font-serif text-3xl sm:text-5xl text-[#1A1A1A] mt-2 leading-tight">
            Conçu pour vous faire <span className="italic">avancer</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => (
            <div key={b.title} className="bg-white rounded-3xl border border-[#EADDC9] p-6 sm:p-7 hover:border-[#C68E59]/50 hover:shadow-[0_8px_28px_-8px_rgba(198,142,89,0.25)] transition" data-testid={`benefit-${i}`}>
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FAF3EA] to-[#F0E0CC] flex items-center justify-center">
                <b.icon className="w-5 h-5 text-[#A26F3E]" />
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mt-5 leading-tight">{b.title}</h3>
              <p className="text-sm text-[#666] mt-2 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ PRICING (single offer) ============ */}
      {!isPremium && !comingSoon && (
        <section className="mb-16" data-testid="pricing-section">
          <div className="max-w-2xl mx-auto rounded-[32px] overflow-hidden bg-gradient-to-br from-[#1A1A1A] to-[#221912] text-white relative">
            <div className="absolute -top-32 -right-32 w-72 h-72 rounded-full bg-[#C68E59]/30 blur-[100px]" />

            <div className="relative p-8 sm:p-12">
              <div className="flex items-center justify-center gap-2">
                <Crown className="w-5 h-5 text-[#C68E59]" />
                <p className="caption-label text-[#E6C29A]">Helencia Premium</p>
              </div>

              <div className="text-center mt-6">
                <div className="flex items-end justify-center gap-1.5">
                  <span className="font-serif text-7xl sm:text-8xl leading-none tracking-tight" data-testid="price-display">29,99€</span>
                  <span className="text-base text-white/70 mb-3">/mois</span>
                </div>
                <p className="text-xs text-[#E6C29A] mt-3 uppercase tracking-[0.2em] inline-flex items-center gap-2">
                  <Shield className="w-3 h-3" /> Sans engagement · Annulable à tout moment
                </p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-3 max-w-md mx-auto text-sm">
                {[
                  "Visibilité prioritaire", "Badge Premium",
                  "Portfolio illimité", "Concours exclusifs",
                  "Accès collaborations 24h avant", "Événements VIP",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-2 text-white/85">
                    <Check className="w-4 h-4 text-[#E6C29A] mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={startCheckout}
                disabled={starting}
                className="w-full mt-10 rounded-full bg-gradient-to-r from-[#D4A574] via-[#C68E59] to-[#A26F3E] hover:from-[#C68E59] hover:to-[#8B5E34] text-white h-14 text-base font-semibold tracking-wide shadow-[0_15px_40px_-15px_rgba(198,142,89,0.7)]"
                data-testid="premium-checkout-btn"
              >
                {starting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirection…</>
                ) : (
                  <><Crown className="w-5 h-5 mr-2" /> Passer Premium <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>

              <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-[11px] text-white/55">
                <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Paiement Stripe sécurisé</span>
                <span className="inline-flex items-center gap-1.5"><Shield className="w-3 h-3" /> SSL · RGPD</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ FINAL SOCIAL PROOF ============ */}
      <section className="rounded-3xl bg-[#FFFBF5] border border-[#EADDC9] p-8 sm:p-10 max-w-4xl mx-auto text-center" data-testid="final-proof">
        <div className="inline-flex items-center gap-1.5">
          {[1,2,3,4,5].map((i) => (
            <Star key={i} className="w-4 h-4 fill-[#C68E59] text-[#C68E59]" />
          ))}
        </div>
        <p className="font-serif italic text-2xl sm:text-3xl text-[#1A1A1A] mt-5 leading-snug">
          « Depuis que je suis Premium, j'ai signé deux campagnes en un mois. La différence est immédiate. »
        </p>
        <p className="text-xs uppercase tracking-[0.2em] text-[#A26F3E] mt-5">
          Léa M. · Talent Premium · Paris
        </p>
      </section>
    </DashboardLayout>
  );
}

function ProofStat({ icon: Icon, value, label }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[#E6C29A]">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white leading-none mt-1">
        {value}
      </div>
      <p className="text-[10px] sm:text-xs uppercase tracking-wider text-white/55 mt-2">{label}</p>
    </div>
  );
}
